import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header1 from '../components/Header1';  // Adjust path if needed
import Footer from '../components/Footer';  // Adjust path if needed
import './Pricing.css';

const Pricing = () => {
    const [loadingTier, setLoadingTier] = useState(null); // Tracks which button is loading
    const [businessSeats, setBusinessSeats] = useState(10); // Default floor minimum of 10
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const handleCheckout = async (tier) => {
        setLoadingTier(tier);
        try {
            const token = localStorage.getItem('token'); // Grab token for auth identification
            
            if (!token) {
                alert('Please log in to purchase a premium subscription.');
                navigate('/login'); // Redirect them to authenticate first
            return;
            }

            // Prepare payload depending on selection
            const payload = { tier };
            if (tier === 'business') {
                payload.quantity = businessSeats;
            }

            const response = await axios.post(
                `${API_URL}/api/create-checkout-session`, 
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            
            if (response.data.url) {
                window.location.href = response.data.url; 
            }
        } catch (error) {
            console.error('Checkout redirect failed:', error);
            alert(error.response?.data || 'Payment initialization failed. Please try again.');
        } finally {
            setLoadingTier(null);
        }
    };

    const handleSeatChange = (e) => {
        const val = parseInt(e.target.value, 10);
        // Prevent typing numbers below 10
        if (isNaN(val) || val < 10) {
            setBusinessSeats(10);
        } else {
            setBusinessSeats(val);
        }
    };

    return (
        <>
            <Header1 />
            <div className="pricing-page-container">
                <div className="pricing-header">
                    <h2>Choose the Right Plan for You</h2>
                    <p>Upgrade to unlock deep data analytics and professional networking tools.</p>
                </div>

                <div className="pricing-grid">
                    {/* 1. FREE PLAN */}
                    <div className="pricing-card">
                        <div className="card-tier-tag">Free</div>
                        <div className="card-price">£0<span>/mo</span></div>
                        <p className="card-desc">Perfect for testing out the waters.</p>
                        <ul className="card-features">
                            <li>✅ Create 2 free cards</li>
                            <li>✅ Share via link or QR code</li>
                            <li>✅ Create meeting backgrounds and email signatures</li>
                            <li>✅ Manage contacts in dashboard</li>
                            <li>❌ Locked analytics dashboard</li>
                            <li>❌ No Hubspot integration</li>
                        </ul>
                        <button className="plan-button free-btn" disabled>Current Tier</button>
                    </div>

                    {/* 2. PRO PLAN */}
                    <div className="pricing-card premium-card">
                        <div className="popular-badge">Most Popular</div>
                        <div className="card-tier-tag">Pro</div>
                        <div className="card-price">£4.99<span>/mo</span></div>
                        <p className="card-desc">For individual professionals seeking raw growth metrics.</p>
                        <ul className="card-features">
                            <li>✅ Create 5 cards</li>
                            <li>✅ Share via link or QR code</li>
                            <li>✅ Create meeting backgrounds and email signatures</li>
                            <li>✅ Full Analytics Dashboard</li>
                            <li>✅ Hubspot integration</li>
                        </ul>
                        <button 
                            onClick={() => handleCheckout('pro')} 
                            disabled={loadingTier !== null}
                            className="plan-button pro-btn"
                        >
                            {loadingTier === 'pro' ? 'Redirecting...' : 'Upgrade to Pro'}
                        </button>
                    </div>

                    {/* 3. BUSINESS PLAN */}
                    <div className="pricing-card">
                        <div className="card-tier-tag">Business</div>
                        <div className="card-price">£2.99<span>/mo per user</span></div>
                        <p className="card-desc">Built for corporate branding and sales teams.</p>
                        
                        {/* Seat counter logic */}
                        <div className="seat-counter-container">
                            <label htmlFor="seats">Number of users:</label>
                            <input 
                                type="number" 
                                id="seats"
                                min="10" 
                                value={businessSeats}
                                onChange={(e) => setBusinessSeats(e.target.value)}
                                onBlur={handleSeatChange}
                            />
                            <span className="min-label">(Minimum 10 seats required)</span>
                        </div>

                        <div className="total-calculation">
                            Total billing: <strong>£{(businessSeats * 2.99).toFixed(2)}/mo</strong>
                        </div>

                        <ul className="card-features">
                            <li>✅ Create unlimited cards</li>
                            <li>✅ Share via link or QR code</li>
                            <li>✅ Create meeting backgrounds and email signatures</li>
                            <li>✅ Full Analytics Dashboard</li>
                            <li>✅ Managed team console dashboard</li>
                            <li>✅ Centralized billing structure</li>
                            <li>✅ Hubspot integration</li>
                            <li>✅ 24/7 dedicated account support</li>
                        </ul>
                        <button 
                            onClick={() => handleCheckout('business')} 
                            disabled={loadingTier !== null}
                            className="plan-button business-btn"
                        >
                            {loadingTier === 'business' ? 'Redirecting...' : 'Deploy Team Tier'}
                        </button>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Pricing;